import * as React from 'react';
import { StepProps, Salutation } from '../../types';
import { useServiceBookingState } from '../context/ServiceBookingState';
import { useTranslation } from 'react-i18next';

export default function Success({ form, close }: StepProps) {
    const { t } = useTranslation();
    const { locations, makes } = useServiceBookingState();

    const selectedLocation = React.useMemo(() => {
        if (!form?.standort_betriebsnummer) return null;
        return locations.find((l) => l.betriebsnummer === form.standort_betriebsnummer) ?? null;
    }, [locations, form?.standort_betriebsnummer]);

    const selectedMake = makes.find((m) => m.fabrikat_code === form.fahrzeug.fabrikat_code) ?? null;

    const formatValue = (v: any): string => {
        if (v === null || v === undefined || v === '') return '—';
        return String(v);
    };

    const formatDate = (isoOrDateLike?: string | null) => {
        if (!isoOrDateLike) return '—';
        try {
            const d = new Date(isoOrDateLike);
            if (Number.isNaN(d.getTime())) return formatValue(isoOrDateLike);
            return d.toLocaleDateString('de-AT', { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch {
            return formatValue(isoOrDateLike);
        }
    };

    const fullName = `${humanSalutationAddress(form?.customer?.anrede!, t)} ${form?.customer?.leading_title} ${
        form?.customer?.first_name
    } ${form?.customer?.last_name}`;

    function formatDateTime(dateTime?: string | null) {
        if (!dateTime) return '—';
        try {
            const d = new Date(dateTime);
            if (Number.isNaN(d.getTime())) return formatValue(dateTime);
            return d.toLocaleString('de-AT', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return formatValue(dateTime);
        }
    }

    return (
        <div className="mx-auto max-w-4xl py-8 print:max-w-none print:p-0">
            {/* Success message */}
            <div className="mb-8 text-center print:mb-6 print:text-left">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center bg-primary print:hidden">
                    <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold text-secondary-100 print:text-xl print:text-black">
                    {t('success.title')}
                </h2>
                <p className="mt-2 text-secondary-300 print:text-gray-600">{t('success.summary')}</p>
            </div>

            {/* Top Row: Fahrzeug and Persönliche Daten cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 print:grid-cols-2 print:gap-4">
                {/* Fahrzeug Card */}
                <div className="flex flex-col border border-secondary-500 bg-white p-6 print:border-gray-300 print:p-4">
                    <h4 className="text-lg font-bold text-secondary-100 print:text-base print:text-black">{t('review.vehicle')}</h4>
                    <div className="mt-4 flex-1 space-y-3 print:mt-2 print:space-y-2">
                        <DataRow label={t('field.make.label')} value={selectedMake?.marke} />
                        <DataRow label={t('field.model.label')} value={formatValue(form?.fahrzeug?.modell)} />
                        <DataRow label={t('field.licensePlate.label')} value={formatValue(form?.fahrzeug?.kennzeichen)} />
                        <DataRow label={t('field.vin.label')} value={formatValue(form?.fahrzeug?.fgstnr)} />
                        <DataRow label={t('field.mileage.label')} value={formatValue(form?.fahrzeug?.km)} />
                        <DataRow label={t('field.firstRegistration.label')} value={formatDate(form?.fahrzeug?.erstzulassung)} />
                    </div>
                </div>

                {/* Persönliche Daten Card */}
                <div className="flex flex-col border border-secondary-500 bg-white p-6 print:border-gray-300 print:p-4">
                    <h4 className="text-lg font-bold text-secondary-100 print:text-base print:text-black">{t('review.personalData')}</h4>
                    <div className="mt-4 flex-1 space-y-3 print:mt-2 print:space-y-2">
                        <DataRow label={t('review.customer')} value={fullName} />
                        <DataRow label={t('field.email.label')} value={formatValue(form?.customer?.email)} />
                        <DataRow label={t('field.phone.label')} value={form?.customer?.telefon_privat || form?.customer?.telefon_mobil || '—'} />
                        <DataRow
                            label={t('review.address')}
                            value={`${formatValue(form?.customer?.street)}, ${formatValue(form?.customer?.post_code)} ${formatValue(form?.customer?.city)}`}
                        />
                    </div>
                </div>
            </div>

            {/* Wunschtermin Highlight Box - includes Standort */}
            <div className="mt-6 border-l-4 border-primary bg-primary/5 p-6 print:mt-4 print:border-l-2 print:border-black print:bg-gray-50 print:p-4">
                <h4 className="text-lg font-bold text-primary print:text-base print:text-black">{t('review.appointment')}</h4>

                {/* Standort info at the top of the box */}
                <div className="mt-4 pb-4 print:mt-2 print:border-gray-300 print:pb-2">
                    <div className="text-sm font-medium text-secondary-300 print:text-xs print:text-gray-600">{t('review.location')}</div>
                    <div className="mt-1 font-semibold text-secondary-100 print:text-black">{selectedLocation?.standort}</div>
                    <div className="text-sm font-semibold text-secondary-300 print:text-gray-600">{selectedLocation?.adresse}</div>
                </div>

                {/* Termin info */}
                <div className="text-sm font-medium text-secondary-300 print:text-xs print:text-gray-600">{t('review.pickup')}</div>
                <div className="mt-1 text-lg font-semibold text-secondary-100 print:text-base print:text-black">
                    {formatDateTime(form?.servicetermin?.annahmezeitpunkt)}
                </div>
            </div>

            {/* Action buttons - hidden on print */}
            <div className="mt-10 flex gap-4 border-t border-secondary-500 pt-6 print:hidden">
                <button type="button" className="button--is-primary" onClick={() => window.print()}>
                    {t('action.print')}
                </button>
                <button type="button" className="button--is-secondary" onClick={close}>
                    {t('action.close')}
                </button>
            </div>
        </div>
    );
}

// Helper Components
function DataRow({ label, value }: { label: string; value?: string | null }) {
    return (
        <div className="print:text-sm">
            <div className="text-xs font-medium uppercase text-secondary-300 print:text-[10px] print:text-gray-600">{label}</div>
            <div className="font-medium text-secondary-100 print:text-black">{value || '—'}</div>
        </div>
    );
}

function humanSalutationAddress(salutation: Salutation, t: (key: string) => string) {
    if (salutation === 'M') {
        return t('field.salutation.mr');
    } else if (salutation === 'F') {
        return t('field.salutation.mrs');
    } else if (salutation === 'FIR') {
        return t('field.salutation.company');
    }
    return '';
}
